import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, useReducedMotion } from 'framer-motion';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left.js';
import MessageCircle from 'lucide-react/dist/esm/icons/message-circle.js';
import { useLanguage } from '@/contexts/LanguageContext';
import { canonicalUrl } from '../seo/siteRoutes.js';

const MethodName = ({ method, language, compact = false }) => {
  if (language === 'en') {
    if (compact || !method.expandedName || method.nameEn === method.expandedName) {
      return <bdi dir="ltr">{method.nameEn}</bdi>;
    }

    return (
      <>
        <bdi dir="ltr">{method.nameEn}</bdi>{' '}
        <span className="text-[0.72em] text-gray-500">
          (<bdi dir="ltr">{method.expandedName}</bdi>)
        </span>
      </>
    );
  }

  if (compact) {
    return method.nameHeLatin ? <bdi dir="ltr">{method.nameHe}</bdi> : method.nameHe;
  }

  return (
    <>
      {method.nameHeLatin ? <bdi dir="ltr">{method.nameHe}</bdi> : method.nameHe}
      {method.expandedName ? (
        <>
          {' '}
          <span className="text-[0.72em] text-gray-500">
            (<bdi dir="ltr">{method.expandedName}</bdi>)
          </span>
        </>
      ) : null}
    </>
  );
};

const FactList = ({ items }) => (
  <ul className="space-y-1.5">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-2.5">
        <span
          aria-hidden="true"
          className="mt-[0.65em] h-1.5 w-1.5 shrink-0 rounded-full bg-[#8500ff]"
        />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const MethodsOfBranding = () => {
  const { language } = useLanguage();
  const prefersReducedMotion = useReducedMotion();
  const isHebrew = language === 'he';
  const direction = isHebrew ? 'rtl' : 'ltr';

  const methods = [
    {
      id: 'dtf',
      key: 'dtf',
      nameHe: 'DTF',
      nameHeLatin: true,
      nameEn: 'DTF',
      expandedName: 'Direct to Film',
      image: '/methods/dtf.jpg',
      imageAlt: isHebrew
        ? 'חולצה שחורה עם הדפס DTF צבעוני'
        : 'Black shirt with a colorful DTF print',
      description: isHebrew
        ? 'בהדפסת DTF העיצוב מודפס תחילה על סרט ייעודי ומועבר לבד באמצעות אבקת דבק, חום ולחץ.'
        : 'DTF uses a printed film that is transferred to garments using adhesive powder, heat and pressure.',
      pros: [
        isHebrew ? 'מתאימה לעיצובים בצבע מלא ולפרטים קטנים' : 'Supports full-color artwork and fine details',
        isHebrew ? 'עשויה להתאים למגוון הרכבי בד' : 'Can suit several fabric compositions',
      ],
      cons: [
        isHebrew ? 'טיפה יותר יקרה ביחס לדפוס משי' : 'Slightly more expensive than screen for very large flat prints',
      ],
      useCases: [
        isHebrew ? 'הדפסות בצבע מלא, כמויות קטנות ובינוניות' : 'Full-colour prints, small-to-medium runs',
      ],
    },
    {
      id: 'dtg',
      key: 'dtg',
      nameHe: 'DTG',
      nameHeLatin: true,
      nameEn: 'DTG',
      expandedName: 'Direct to Garment',
      image: '/methods/dtg.jpg',
      imageAlt: isHebrew
        ? 'הדפסת DTG צבעונית בתהליך ייצור'
        : 'Colorful DTG print during production',
      description: isHebrew
        ? 'DTG מדפיס ישירות על הבגד באמצעות מדפסות דיו טקסטיל. מתאים במיוחד לכותנה בהירה ועיצובים מפורטים.'
        : 'DTG prints directly onto garments using textile inkjet printers and can suit detailed, colorful designs on light cotton.',
      pros: [
        isHebrew ? 'שכבת הדפס שאינה מבוססת על סרט העברה' : 'No transfer-film layer',
        isHebrew ? 'מתאימה לפרטים קטנים ולמעברי צבע' : 'Supports fine detail and color gradients',
      ],
      cons: [
        isHebrew ? 'מתאימה בעיקר לכותנה או בדים בעלי אחוז כותנה גבוה' : 'Not ideal for dark garments unless pre-treated',
      ],
      useCases: [
        isHebrew ? 'הדפסות אומנותיות, תמונות והזמנות אישיות' : 'Art prints, photos, and custom single items',
      ],
    },
    {
      id: 'screen-printing',
      key: 'screen',
      nameHe: 'הדפסה במסך',
      nameHeLatin: false,
      nameEn: 'Screen printing',
      expandedName: 'Screen printing',
      image: '/methods/screen_printing.jpg',
      imageAlt: isHebrew ? 'תהליך הדפסה במסך' : 'Screen printing process',
      description: isHebrew
        ? 'בהדפסה במסך מכינים מסך לכל צבע ומניחים את הדיו בשכבות. השיטה עשויה להתאים להזמנות גדולות עם מספר צבעים מוגבל.'
        : 'Screen printing uses a stencil for each color to apply ink in layers and can suit larger runs with a limited color count.',
      pros: [
        isHebrew ? 'כיסוי צבע בולט' : 'Strong ink coverage',
      ],
      cons: [
        isHebrew ? 'הכנות מסכים יכולות להיות יקרות להרצות קטנות' : 'Higher setup costs make small runs less economical',
      ],
      useCases: [
        isHebrew ? 'חולצות אחידות, הזמנות גדולות ומוצרים ממותגים' : 'Uniforms, bulk orders, merchandising',
      ],
    },
    {
      id: 'sublimation',
      key: 'sublimation',
      nameHe: 'סובלימציה',
      nameHeLatin: false,
      nameEn: 'Sublimation',
      expandedName: 'Sublimation',
      image: '/methods/sublimation.jpg',
      imageAlt: isHebrew
        ? 'תקריב של הדפסת סובלימציה על בד'
        : 'Close-up of sublimation printing on fabric',
      description: isHebrew
        ? 'בסובלימציה העיצוב מודפס על נייר מיוחד ומועבר בחום לבד או מוצר מצופה; הדיו חודר לסיבים והופך לחלק מהחומר.'
        : 'Sublimation transfers dye into polyester fibers or a compatible coating using heat.',
      pros: [
        isHebrew ? 'הצבע נטמע בחומר המתאים ללא שכבת הדפס בולטת' : 'The dye integrates into a compatible material without a raised print layer',
      ],
      cons: [
        isHebrew ? 'עובד רק על בדים פוליאסטריים ובהירים' : 'Only works on polyester or polymer-coated substrates',
      ],
      useCases: [
        isHebrew ? 'בגדי ספורט, חולצות טכניות ומוצרים מצופים' : 'Polyester shirts, sportswear, sublimated signage',
      ],
    },
    {
      id: 'embroidery',
      key: 'embroidery',
      nameHe: 'רקמה',
      nameHeLatin: false,
      nameEn: 'Embroidery',
      expandedName: 'Embroidery',
      image: '/methods/embroidery.jpg',
      imageAlt: isHebrew ? 'מכונת רקמה בתהליך עבודה' : 'Embroidery machine at work',
      description: isHebrew
        ? 'רקמה ממוחשבת יוצרת את העיצוב באמצעות תפרים צבעוניים ומעניקה לו מרקם בולט.'
        : 'Computerized embroidery builds the design from colored stitches and gives it a raised texture.',
      pros: [
        isHebrew ? 'מראה תפור ובולט' : 'A stitched, raised appearance',
      ],
      cons: [
        isHebrew ? 'מגבלת פרטים קטנים או גוונים משתנים' : 'Not ideal for photographic detail or very fine artwork',
      ],
      useCases: [
        isHebrew ? 'כובעים, חולצות פולו ופריטים שמתאימים לרקמה' : 'Hats, polo shirts and items suited to embroidery',
      ],
    },
  ];

  const siteBase = canonicalUrl('/methods-of-branding');

  const faqJson = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: isHebrew ? 'מה ההבדל בין DTG ל-DTF?' : 'What is the difference between DTG and DTF?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: isHebrew
            ? 'DTG מדפיס ישירות על הבגד; DTF משתמש בסרט העברה ועשוי להתאים להרכבי בד נוספים, בהתאם למוצר ולעיצוב.'
            : 'DTG prints directly on the garment; DTF uses a transfer film and may suit additional fabric compositions, depending on the product and artwork.',
        },
      },
      {
        '@type': 'Question',
        name: isHebrew ? 'מתי כדאי לבחור הדפסה במסך?' : 'When should I choose screen printing?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: isHebrew
            ? 'בהזמנה גדולה עם מספר צבעים מוגבל, הדפסה במסך עשויה להתאים לתהליך הייצור.'
            : 'For a larger order with a limited color count, screen printing may suit the production process.',
        },
      },
    ],
  };

  const howToJson = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: isHebrew ? 'כיצד לבחור שיטת הדפסה' : 'How to choose a printing method',
    description: isHebrew
      ? 'מדריך קצר לבחירת שיטת מיתוג לפי המוצר, החומר והעיצוב'
      : 'A short guide to choosing a branding method by product, material and artwork',
    step: [
      {
        '@type': 'HowToStep',
        url: `${siteBase}#dtf`,
        name: 'DTF',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: isHebrew
              ? 'עשויה להתאים לעיצוב בצבע מלא על הרכבי בד שונים'
              : 'May suit full-color artwork on different fabric compositions',
          },
        ],
      },
      {
        '@type': 'HowToStep',
        url: `${siteBase}#dtg`,
        name: 'DTG',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: isHebrew
              ? 'עשויה להתאים לעיצובים מפורטים על פריטי כותנה'
              : 'May suit detailed artwork on cotton garments',
          },
        ],
      },
      {
        '@type': 'HowToStep',
        url: `${siteBase}#screen-printing`,
        name: isHebrew ? 'הדפסה במסך' : 'Screen printing',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: isHebrew
              ? 'בחירה לטווחים גדולים עם כיסוי צבע חזק'
              : 'Choose for large runs with strong ink coverage',
          },
        ],
      },
      {
        '@type': 'HowToStep',
        url: `${siteBase}#sublimation`,
        name: isHebrew ? 'סובלימציה' : 'Sublimation',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: isHebrew
              ? 'מתאים להדפסות all-over על חומרים סינתטיים תואמים'
              : 'Suitable for all-over prints on compatible synthetic materials',
          },
        ],
      },
      {
        '@type': 'HowToStep',
        url: `${siteBase}#embroidery`,
        name: isHebrew ? 'רקמה' : 'Embroidery',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: isHebrew
              ? 'מראה תפור ובולט ללוגו ולטקסט'
              : 'A stitched, raised appearance for logos and text',
          },
        ],
      },
    ],
  };

  const revealFromBelow = (delay = 0) => ({
    initial: prefersReducedMotion ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.18 },
    transition: prefersReducedMotion ? { duration: 0 } : { duration: 0.48, delay, ease: 'easeOut' },
  });

  const heroEntrance = (delay = 0) => ({
    initial: prefersReducedMotion ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: prefersReducedMotion ? { duration: 0 } : { duration: 0.5, delay, ease: 'easeOut' },
  });

  const labels = {
    best: isHebrew ? 'מתאים ל' : 'Suitable for',
    advantage: isHebrew ? 'היתרון המרכזי' : 'Main advantage',
    note: isHebrew ? 'חשוב לדעת' : 'Important to know',
  };

  return (
    <div dir={direction} className="bg-white text-[#15131a]">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJson)}</script>
        <script type="application/ld+json">{JSON.stringify(howToJson)}</script>
      </Helmet>

      <section aria-labelledby="methods-hero-title" className="overflow-hidden bg-[#0c0b0f] text-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:min-h-[590px] lg:grid-cols-[0.88fr_1.12fr] lg:gap-16 lg:px-8 lg:py-20">
          <motion.div {...heroEntrance(0.04)} className="max-w-2xl text-start">
            <p className="mb-5 text-sm font-medium tracking-[0.08em] text-[#d6b5ff] sm:text-base">
              {isHebrew ? 'מדריך מיתוג והדפסה' : 'Branding & printing guide'}
            </p>
            <h1
              id="methods-hero-title"
              className="max-w-[17ch] text-4xl font-normal leading-[1.03] tracking-[-0.035em] sm:text-5xl lg:text-[3.25rem]"
            >
              {isHebrew
                ? 'לא בטוחים איזו שיטת מיתוג מתאימה? אנחנו נתאים אותה להזמנה.'
                : 'Not sure which branding method fits? We will match it to your order.'}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-8 text-white/[0.72]">
              {isHebrew
                ? 'סוג הבד, כמות ההזמנה ואופי העיצוב עוזרים לנו לבחור את השיטה הנכונה לעבודה.'
                : 'Fabric, order volume and artwork details help us choose the right method for the job.'}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/catalog"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-gradient-to-l from-[#fe00c7] to-[#7900ff] px-7 py-3 text-base font-medium text-white shadow-[0_14px_34px_rgba(155,0,255,0.3)] transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white motion-reduce:transform-none motion-reduce:transition-none"
              >
                {isHebrew ? 'בחרו בגד' : 'Choose a garment'}
                <ArrowLeft aria-hidden="true" className={`h-4 w-4 ${isHebrew ? '' : 'rotate-180'}`} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-white/[0.35] px-6 py-3 text-base text-white transition-colors duration-200 hover:border-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white motion-reduce:transition-none"
              >
                <MessageCircle aria-hidden="true" className="h-4 w-4" />
                {isHebrew ? 'התייעצו איתנו' : 'Talk to us'}
              </Link>
            </div>
          </motion.div>

          <motion.figure {...heroEntrance(0.12)} className="relative">
            <div className="relative aspect-[6/5] overflow-hidden rounded-[1.75rem] bg-[#19171e] sm:aspect-[4/3] lg:aspect-[5/4]">
              <img
                src="/methods/dtf.jpg"
                alt={methods[0].imageAlt}
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.02] motion-reduce:transform-none motion-reduce:transition-none"
                loading="eager"
                decoding="async"
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent"
              />
              <figcaption className="absolute bottom-0 start-0 m-4 rounded-full bg-black/[0.72] px-4 py-2 text-sm text-white backdrop-blur-sm sm:m-5">
                <bdi dir="ltr">DTF · Direct to Film</bdi>
              </figcaption>
            </div>
            <div
              aria-hidden="true"
              className="absolute -bottom-10 -start-10 -z-10 h-44 w-44 rounded-full bg-[#8c00ff]/[0.35] blur-3xl"
            />
          </motion.figure>
        </div>
      </section>

      <nav
        aria-label={isHebrew ? 'ניווט בין שיטות המיתוג' : 'Branding methods navigation'}
        className="border-b border-black/10 bg-white/95 backdrop-blur-md sm:sticky sm:top-[var(--header-height)] sm:z-30"
      >
        <div className="mx-auto flex max-w-5xl gap-2 overflow-x-auto px-4 py-3 sm:grid sm:grid-cols-5 sm:overflow-visible sm:px-6 lg:px-8">
          {methods.map((method) => (
            <a
              key={method.key}
              href={`#${method.id}`}
              className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-full px-4 py-2 text-center text-sm text-gray-700 transition-colors duration-200 hover:bg-[#f4ebff] hover:text-[#6f00d9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7a00ff] motion-reduce:transition-none sm:px-3"
            >
              <MethodName method={method} language={language} compact />
            </a>
          ))}
        </div>
      </nav>

      <section aria-labelledby="methods-comparison-title" className="bg-white py-16 sm:py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div {...revealFromBelow()} className="max-w-2xl text-start">
            <p className="text-sm font-medium tracking-[0.08em] text-[#7a00ff]">
              {isHebrew ? 'במבט אחד' : 'At a glance'}
            </p>
            <h2
              id="methods-comparison-title"
              className="mt-3 text-3xl font-normal leading-tight tracking-[-0.025em] sm:text-4xl lg:text-5xl"
            >
              {isHebrew ? 'כל שיטה מצטיינת במקום אחר' : 'Every method has its place'}
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              {isHebrew
                ? 'השוו במהירות בין סוג העבודה המתאים לכל שיטה לבין המגבלה העיקרית שלה.'
                : 'Quickly compare the type of work each method suits with its main limitation.'}
            </p>
          </motion.div>

          <motion.ul {...revealFromBelow(0.05)} className="mt-10 border-y border-black/[0.12]" role="list">
            {methods.map((method, index) => (
              <li
                key={method.key}
                className="grid gap-5 border-b border-black/10 py-6 last:border-b-0 md:grid-cols-[0.7fr_1.15fr_1.15fr] md:items-start md:gap-8"
              >
                <a
                  href={`#${method.id}`}
                  className="group inline-flex items-baseline gap-3 text-xl text-[#15131a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#7a00ff]"
                >
                  <span className="text-sm tabular-nums text-[#8b00ff]">{String(index + 1).padStart(2, '0')}</span>
                  <span className="border-b border-transparent transition-colors duration-200 group-hover:border-[#8b00ff] motion-reduce:transition-none">
                    <MethodName method={method} language={language} compact />
                  </span>
                </a>
                <div>
                  <p className="mb-1 text-xs font-medium tracking-[0.06em] text-gray-500">{labels.best}</p>
                  <p className="text-base leading-7 text-gray-800">{method.useCases[0]}</p>
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium tracking-[0.06em] text-gray-500">{labels.note}</p>
                  <p className="text-base leading-7 text-gray-800">{method.cons[0]}</p>
                </div>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      <div className="bg-[#f7f5f8]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {methods.map((method, index) => (
            <motion.section
              key={method.key}
              id={method.id}
              aria-labelledby={`${method.id}-title`}
              {...revealFromBelow()}
              className="scroll-mt-[calc(var(--header-height)+96px)] border-t border-black/10 py-16 first:border-t-0 sm:py-20 lg:py-24"
            >
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
                <figure className={index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}>
                  <div className="group relative aspect-[4/3] overflow-hidden rounded-[1.75rem] bg-[#e9e6ec]">
                    <img
                      src={method.image}
                      alt={method.imageAlt}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.025] motion-reduce:transform-none motion-reduce:transition-none"
                      loading="lazy"
                      decoding="async"
                    />
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/10"
                    />
                  </div>
                </figure>

                <div className={`text-start ${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                  <p className="text-sm font-medium tabular-nums tracking-[0.08em] text-[#7a00ff]">
                    {isHebrew ? 'שיטת מיתוג' : 'Branding method'} {String(index + 1).padStart(2, '0')}
                  </p>
                  <h2
                    id={`${method.id}-title`}
                    className="mt-3 text-3xl font-normal leading-tight tracking-[-0.025em] sm:text-4xl lg:text-5xl"
                  >
                    <MethodName method={method} language={language} />
                  </h2>
                  <p className="mt-5 max-w-2xl text-[17px] leading-8 text-gray-600 sm:text-lg">
                    {method.description}
                  </p>

                  <dl className="mt-8 border-y border-black/[0.12]">
                    <div className="grid gap-2 border-b border-black/10 py-5 sm:grid-cols-[9rem_1fr] sm:gap-6">
                      <dt className="text-sm font-medium text-gray-500">{labels.best}</dt>
                      <dd className="text-base leading-7 text-gray-900">
                        <FactList items={method.useCases} />
                      </dd>
                    </div>
                    <div className="grid gap-2 border-b border-black/10 py-5 sm:grid-cols-[9rem_1fr] sm:gap-6">
                      <dt className="text-sm font-medium text-gray-500">{labels.advantage}</dt>
                      <dd className="text-base leading-7 text-gray-900">
                        <FactList items={method.pros} />
                      </dd>
                    </div>
                    <div className="grid gap-2 py-5 sm:grid-cols-[9rem_1fr] sm:gap-6">
                      <dt className="text-sm font-medium text-gray-500">{labels.note}</dt>
                      <dd className="text-base leading-7 text-gray-900">
                        <FactList items={method.cons} />
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </motion.section>
          ))}
        </div>
      </div>

      <section className="bg-gradient-to-l from-[#7600ff] to-[#f000c8] text-white">
        <motion.div
          {...revealFromBelow()}
          className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-8 px-4 py-14 sm:px-6 sm:py-16 lg:flex-row lg:items-center lg:px-8 lg:py-20"
        >
          <div className="max-w-2xl text-start">
            <h2 className="text-3xl font-normal leading-tight tracking-[-0.025em] sm:text-4xl lg:text-5xl">
              {isHebrew
                ? 'מעלים עיצוב, בוחרים בגד, ואנחנו מתאימים את שיטת המיתוג.'
                : 'Upload your artwork, choose a garment, and we will match the branding method.'}
            </h2>
            <p className="mt-4 text-lg leading-8 text-white/80">
              {isHebrew
                ? 'ספרו לנו מה אתם מכינים ונעזור לחבר בין המוצר, העיצוב והשיטה.'
                : 'Tell us what you are making and we will help connect the product, artwork and method.'}
            </p>
          </div>

          <div className="flex w-full flex-wrap gap-3 lg:w-auto lg:shrink-0">
            <Link
              to="/catalog"
              className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-full bg-white px-7 py-3 text-base font-medium text-[#5300a9] transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white motion-reduce:transform-none motion-reduce:transition-none sm:flex-none"
            >
              {isHebrew ? 'בחרו בגד' : 'Choose a garment'}
              <ArrowLeft aria-hidden="true" className={`h-4 w-4 ${isHebrew ? '' : 'rotate-180'}`} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex min-h-12 flex-1 items-center justify-center rounded-full border border-white/[0.55] px-7 py-3 text-base text-white transition-colors duration-200 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white motion-reduce:transition-none sm:flex-none"
            >
              {isHebrew ? 'דברו איתנו' : 'Contact us'}
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default MethodsOfBranding;
